import type { CampaignClaimKey } from "./campaignCodes";

import { getCampaignCodeEntry } from "./campaignCodes";

// NOTE (Quinn, 2026-08-28): campaign links arrive with ?source=<code>; matching
// the welcome copy to the promise that earned the click is the message-match
// lever from the ads GTM plan. Codes decode through campaignCodes.ts; unknown
// or absent codes fall through to each surface's default (intro video on the
// welcome screen, the miniapps hero on the signup panel).
export type CampaignWelcomeContent = {
  headline: string;
  subtitle: string;
  gifSrc: string;
  gifAlt: string;
};

// Named so other surfaces (the cancellation offer) can reuse a claim without knowing campaign codes.
const landingPageClaims = {
  adToPage: {
    headline: "Turn your ads into landing pages",
    subtitle:
      "Drop an ad into the chat and the agent builds the matching page: same hook, same offer, ready to publish.",
    gifSrc:
      "https://public.replocdn.com/misc/positioning-test-w1/ad-to-landing-r3.gif",
    gifAlt: "An ad dropped into Replo chat becoming a matching landing page",
  },
  agentSetup: {
    headline: "Put an AI agent to work on your store",
    subtitle:
      "Point it at your site and Replo builds your brand kit, then we help you wire up your first workflows.",
    gifSrc:
      "https://public.replocdn.com/misc/positioning-test-w1/agent-setup-r3.gif",
    gifAlt:
      "Replo Branding reading a store's website and building its brand kit",
  },
  tenPages: {
    headline: "Build 10 landing pages in an afternoon",
    subtitle:
      "The agent builds from your own sections and brand, ready for paid traffic. No dev or design queue.",
    gifSrc:
      "https://public.replocdn.com/misc/positioning-test-w1/agent-hero-r3.gif",
    gifAlt:
      "One prompt in Replo chat building a launch page on the store's real brand",
  },
  conversionAudit: {
    headline: "Find what's hurting conversion",
    subtitle:
      "The agent audits your landing pages, shows you what's leaking, and builds variants to test.",
    gifSrc:
      "https://public.replocdn.com/misc/positioning-test-w1/audit-flow-r3.gif",
    gifAlt: "Replo Agent auditing a landing page and testing fix variants",
  },
  quizFunnel: {
    headline: "Get a quiz funnel ready for Black Friday",
    subtitle:
      "The agent builds the quiz, recommends your real products, and wires email capture to your list. Ask it for a Black Friday product-finder quiz.",
    gifSrc:
      "https://public.replocdn.com/misc/positioning-test-w1/agent-hero-r3.gif",
    gifAlt: "Replo Agent building a product quiz funnel from a prompt",
  },
  bundleBuilder: {
    headline: "Get a bundle builder ready for Black Friday",
    subtitle:
      "The agent builds a pick-your-items bundle page from your real catalog, discount applied, straight into checkout. Ask it to build one from a collection URL.",
    gifSrc:
      "https://public.replocdn.com/misc/positioning-test-w1/ad-to-landing-r3.gif",
    gifAlt: "Replo Agent building a build-your-own-bundle page from a prompt",
  },
} satisfies Record<CampaignClaimKey, CampaignWelcomeContent>;

export function getCampaignWelcomeContent(
  source: string | null,
): CampaignWelcomeContent | null {
  const entry = getCampaignCodeEntry(source);
  return entry ? landingPageClaims[entry.claim] : null;
}
